import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Play, Pause, ArrowsClockwise, MapPin, Thermometer, Lightbulb, WifiSlash,
  Clock, Flag, FlagPennant, ListChecks, Path, Info, Crosshair,
} from "@phosphor-icons/react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALERT_KIND_STYLES = {
  temp:    { color: "#EF4444", icon: Thermometer, label: "Temperature" },
  light:   { color: "#F59E0B", icon: Lightbulb,   label: "Light/Seal" },
  delay:   { color: "#A855F7", icon: Clock,       label: "Delay" },
  offline: { color: "#71717A", icon: WifiSlash,   label: "Offline" },
  battery: { color: "#F59E0B", icon: Lightbulb,   label: "Battery Low" },
  other:   { color: "#A855F7", icon: Info,        label: "Other" },
};

const TIME_FILTERS = [
  { key: "live",  label: "Live" },
  { key: "1h",    label: "1h",   ms: 3600_000 },
  { key: "6h",    label: "6h",   ms: 21600_000 },
  { key: "24h",   label: "24h",  ms: 86400_000 },
  { key: "full",  label: "Full" },
];

export default function RouteView({ shipmentId }) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const tileLayerRef = useRef(null);
  const layersRef = useRef({});  // keep so we can clear

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("full");
  const [playing, setPlaying] = useState(false);
  const [playIdx, setPlayIdx] = useState(0);
  const [hoverIdx, setHoverIdx] = useState(null);
  const [legendOpen, setLegendOpen] = useState(true);
  const [basemap, setBasemap] = useState("dark");

  const fetchRoute = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/shipments/${shipmentId}/route`);
      setData(res.data);
      // Allow re-fit on next layer render (new shipment loaded)
      if (layersRef.current) layersRef.current._didInitialFit = false;
    } catch (e) { /* noop */ }
    finally { setLoading(false); }
  }, [shipmentId]);

  useEffect(() => { fetchRoute(); }, [fetchRoute]);

  // Filter points by time window
  const filteredPoints = useMemo(() => {
    if (!data?.actual?.points) return [];
    const points = data.actual.points;
    if (filter === "full" || points.length === 0) return points;
    if (filter === "live") return points.slice(-1);  // just last
    const filterCfg = TIME_FILTERS.find(f => f.key === filter);
    if (!filterCfg?.ms) return points;
    const last = new Date(points[points.length - 1].timestamp).getTime();
    return points.filter(p => last - new Date(p.timestamp).getTime() <= filterCfg.ms);
  }, [data, filter]);

  // Initialize map (StrictMode-safe: uses unique check on the div + idempotent cleanup)
  useEffect(() => {
    const node = mapRef.current;
    if (!node) return;
    if (node._leaflet_id) return;
    try {
      const map = L.map(node, { zoomControl: true, attributionControl: false })
        .setView([39.5, -98.5], 4);
      tileLayerRef.current = L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19, className: "dark-osm-tiles", attribution: "&copy; OpenStreetMap contributors" }).addTo(map);
      mapInstance.current = map;
      setTimeout(() => { try { map.invalidateSize(); } catch (e) { /* noop */ } }, 100);
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => {
    const map = mapInstance.current;
    if (!map || !tileLayerRef.current) return;
    map.removeLayer(tileLayerRef.current);
    const satellite = basemap === "satellite";
    tileLayerRef.current = L.tileLayer(
      satellite ? "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      { maxZoom: 19, className: satellite ? "" : "dark-osm-tiles", attribution: satellite ? "Imagery &copy; Esri, Maxar, Earthstar Geographics" : "&copy; OpenStreetMap contributors" }
    ).addTo(map);
    tileLayerRef.current.bringToBack?.();
  }, [basemap]);

  // Cleanup on unmount
  useEffect(() => () => {
    if (mapInstance.current) {
      try { mapInstance.current.remove(); } catch (e) { /* noop */ }
      mapInstance.current = null;
      layersRef.current = {};
    }
  }, []);

  // Render layers when data changes
  useEffect(() => {
    const map = mapInstance.current;
    if (!map) return;
    // Force resize check first (covers tab switches and StrictMode)
    setTimeout(() => { try { map.invalidateSize(); } catch (e) { /* noop */ } }, 50);
    if (!data) return;

    // Clear all previously added layers — handle both individual layers and groups
    Object.values(layersRef.current).forEach((entry) => {
      if (!entry) return;
      const list = Array.isArray(entry) ? entry : [entry];
      list.forEach((l) => { try { map.removeLayer(l); } catch (e) { /* noop */ } });
    });
    layersRef.current = {};

    // Planned route — faint dashed line origin → destination
    const planned = data.planned;
    if (planned.origin?.lat != null && planned.destination?.lat != null) {
      const plannedLine = L.polyline(
        [[planned.origin.lat, planned.origin.lng], [planned.destination.lat, planned.destination.lng]],
        { color: "#71717A", weight: 2, dashArray: "6 8", opacity: 0.4 }
      ).addTo(map);
      layersRef.current.planned = plannedLine;

      // Origin — large glowing teal circle with truck icon
      layersRef.current.origin = L.marker([planned.origin.lat, planned.origin.lng], {
        icon: L.divIcon({
          html: `<div style="position:relative;width:32px;height:32px;">
            <div style="position:absolute;inset:-8px;border-radius:50%;background:radial-gradient(closest-side,#14B8A6aa,transparent);"></div>
            <div style="position:absolute;inset:0;border-radius:50%;background:#14B8A6;border:3px solid #0a0a0a;display:flex;align-items:center;justify-content:center;font-size:14px;color:#0a0a0a;font-weight:bold">▶</div>
          </div>`,
          className: "", iconSize: [32, 32], iconAnchor: [16, 16],
        }),
      }).bindPopup(`<b>Origin</b><br/>${planned.origin.address || "—"}`).addTo(map);

      layersRef.current.dest = L.marker([planned.destination.lat, planned.destination.lng], {
        icon: L.divIcon({
          html: `<div style="position:relative;width:32px;height:32px;">
            <div style="position:absolute;inset:-8px;border-radius:50%;background:radial-gradient(closest-side,#14B8A6aa,transparent);"></div>
            <div style="position:absolute;inset:0;border-radius:50%;background:#14B8A6;border:3px solid #0a0a0a;display:flex;align-items:center;justify-content:center;font-size:14px;color:#0a0a0a;font-weight:bold">⚑</div>
          </div>`,
          className: "", iconSize: [32, 32], iconAnchor: [16, 16],
        }),
      }).bindPopup(`<b>Destination</b><br/>${planned.destination.address || "—"}`).addTo(map);
    }

    if (filteredPoints.length === 0) {
      // Auto-fit on planned only
      if (planned.origin?.lat != null && planned.destination?.lat != null) {
        map.fitBounds([
          [planned.origin.lat, planned.origin.lng],
          [planned.destination.lat, planned.destination.lng],
        ], { padding: [40, 40] });
      }
      return;
    }

    const allCoords = filteredPoints.map(p => [p.lat, p.lng]);
    const visibleEndIdx = playing ? Math.min(playIdx, filteredPoints.length - 1) : filteredPoints.length - 1;

    // 1. Solid line for completed (up to visibleEndIdx) — with glow underlay
    if (visibleEndIdx > 0) {
      const completedCoords = allCoords.slice(0, visibleEndIdx + 1);
      const glow = L.polyline(completedCoords, { color: "#14B8A6", weight: 12, opacity: 0.18 }).addTo(map);
      const completedLine = L.polyline(completedCoords, {
        color: "#14B8A6", weight: 5, opacity: 1.0,
      }).addTo(map);
      // Track BOTH polylines as an array so cleanup removes both
      layersRef.current.completed = [glow, completedLine];

      // Red overlays for alert sub-segments
      const redSegments = [];
      let currentSeg = null;
      for (let i = 1; i <= visibleEndIdx; i++) {
        if (filteredPoints[i].has_alert || filteredPoints[i - 1].has_alert) {
          if (!currentSeg) currentSeg = [allCoords[i - 1]];
          currentSeg.push(allCoords[i]);
        } else if (currentSeg) {
          if (currentSeg.length > 1) redSegments.push(currentSeg);
          currentSeg = null;
        }
      }
      if (currentSeg && currentSeg.length > 1) redSegments.push(currentSeg);
      if (redSegments.length) {
        const redLines = redSegments.map(seg =>
          L.polyline(seg, { color: "#EF4444", weight: 6, opacity: 0.95 }).addTo(map)
        );
        layersRef.current.alertSegs = redLines;
      }
    }

    // 2. Dashed line for remaining
    if (visibleEndIdx < filteredPoints.length - 1) {
      const remaining = allCoords.slice(visibleEndIdx);
      const dashed = L.polyline(remaining, {
        color: "#14B8A6", weight: 3, opacity: 0.6, dashArray: "10 8",
      }).addTo(map);
      layersRef.current.remaining = dashed;
    }

    // 3. Current position pulsing marker
    const cur = filteredPoints[visibleEndIdx];
    if (cur) {
      const curMarker = L.marker([cur.lat, cur.lng], {
        icon: L.divIcon({
          html: `<div style="position:relative;width:24px;height:24px;">
            <div style="position:absolute;inset:0;border-radius:50%;background:#14B8A6;animation:rv-pulse 1.4s infinite;opacity:.5"></div>
            <div style="position:absolute;inset:6px;border-radius:50%;background:#14B8A6;border:2px solid #0a0a0a;box-shadow:0 0 12px #14B8A6"></div>
          </div>
          <style>@keyframes rv-pulse{0%{transform:scale(.9);opacity:.7}100%{transform:scale(2.2);opacity:0}}</style>`,
          className: "", iconSize: [24, 24], iconAnchor: [12, 12],
        }),
      }).bindPopup(`<b>${cur.device_name}</b><br/>${(cur.timestamp || "").substring(0, 19)}<br/>Temp: ${cur.temperature}°F`).addTo(map);
      layersRef.current.current = curMarker;
    }

    // 4. Hover marker
    if (hoverIdx != null && filteredPoints[hoverIdx]) {
      const h = filteredPoints[hoverIdx];
      layersRef.current.hover = L.circleMarker([h.lat, h.lng], {
        radius: 9, color: "#FCD34D", fillColor: "#FCD34D", fillOpacity: 0.6, weight: 2,
      }).addTo(map);
    }

    // 5. Alert markers (kind-coded)
    if (data.alert_markers?.length) {
      const alertMarkers = data.alert_markers.map((a) => {
        const style = ALERT_KIND_STYLES[a.kind] || ALERT_KIND_STYLES.other;
        return L.marker([a.lat, a.lng], {
          icon: L.divIcon({
            html: `<div style="background:${style.color};color:#fff;width:18px;height:18px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:2px solid #0a0a0a;box-shadow:0 0 6px ${style.color}88;font-size:9px;font-weight:bold">!</div>`,
            className: "", iconSize: [18, 18],
          }),
        }).bindPopup(`<b style="color:${style.color}">${style.label}</b><br/>${a.message}<br/><small>${(a.timestamp || "").substring(0, 19)}</small>`).addTo(map);
      });
      layersRef.current.alertMarkers = alertMarkers;
    }

    // Force resize check (handles tab-switch / StrictMode)
    setTimeout(() => { try { map.invalidateSize(); } catch (e) { /* noop */ } }, 50);

    // Auto-fit map (only do this on initial data load, not every hover)
    if (!layersRef.current._didInitialFit) {
      try {
        const all = [...allCoords];
        if (planned.origin?.lat != null) all.push([planned.origin.lat, planned.origin.lng]);
        if (planned.destination?.lat != null) all.push([planned.destination.lat, planned.destination.lng]);
        if (all.length > 1) map.fitBounds(all, { padding: [40, 40], maxZoom: 14 });
        else if (allCoords.length === 1) map.setView(allCoords[0], 11);
        layersRef.current._didInitialFit = true;
      } catch (e) { /* noop */ }
    }
  }, [data, filteredPoints, playing, playIdx, hoverIdx]);

  // Playback animation
  useEffect(() => {
    if (!playing || filteredPoints.length === 0) return;
    if (playIdx >= filteredPoints.length - 1) { setPlaying(false); return; }
    const id = setTimeout(() => setPlayIdx((i) => Math.min(i + 1, filteredPoints.length - 1)), 80);
    return () => clearTimeout(id);
  }, [playing, playIdx, filteredPoints.length]);

  // Reset playback when filter changes
  useEffect(() => { setPlayIdx(filteredPoints.length - 1); setPlaying(false); }, [filter, filteredPoints.length]);

  if (!data && !loading) return <div className="text-xs text-zinc-500">No route data</div>;

  const p = data?.progress || { percent_complete: null, miles_completed: 0, miles_total: null, eta_delta_hours: null, is_delivered: false };
  const pts = filteredPoints;

  const focusPoint = (idx) => {
    setHoverIdx(idx);
    const pt = pts[idx];
    if (pt && mapInstance.current) mapInstance.current.flyTo([pt.lat, pt.lng], 13, { duration: 0.5 });
  };

  // User-triggered recenter: fits bounds to current filtered route + planned endpoints, smooth animation
  const recenter = () => {
    const map = mapInstance.current;
    if (!map || !data) return;
    const planned = data.planned;
    const coords = pts.map(p => [p.lat, p.lng]);
    if (planned.origin?.lat != null) coords.push([planned.origin.lat, planned.origin.lng]);
    if (planned.destination?.lat != null) coords.push([planned.destination.lat, planned.destination.lng]);
    if (coords.length === 0) return;
    if (coords.length === 1) {
      map.flyTo(coords[0], 11, { duration: 0.6 });
      return;
    }
    try {
      const bounds = L.latLngBounds(coords);
      map.flyToBounds(bounds, { padding: [40, 40], maxZoom: 14, duration: 0.6 });
    } catch (e) { /* noop */ }
  };

  return (
    <div className="space-y-3" data-testid="route-view">
      {/* Status / progress bar */}
      <Card className="bg-zinc-900 border-zinc-800 p-4">
        <div className="flex items-center gap-6 flex-wrap">
          <ProgressKPI label="Complete" value={p.percent_complete != null ? `${p.percent_complete}%` : "—"}
            color="text-teal-400" />
          <ProgressKPI label="Miles" value={p.miles_total != null ? `${p.miles_completed} / ${p.miles_total}` : `${p.miles_completed}`} color="text-zinc-200" />
          <ProgressKPI label="Points" value={data?.actual?.points?.length ?? 0} color="text-zinc-200" />
          <ProgressKPI label="Alerts" value={data?.alert_markers?.length ?? 0}
            color={(data?.alert_markers?.length ?? 0) > 0 ? "text-red-400" : "text-emerald-400"} />
          <ProgressKPI label="ETA Delta"
            value={p.eta_delta_hours != null ? `${p.eta_delta_hours > 0 ? "+" : ""}${p.eta_delta_hours}h` : "—"}
            color={p.eta_delta_hours == null ? "text-zinc-300" :
                   p.eta_delta_hours > 0 ? "text-amber-400" : "text-emerald-400"} />
          <div className="flex-1" />
          <button onClick={fetchRoute} className="text-zinc-500 hover:text-teal-400" title="Refresh">
            <ArrowsClockwise size={14} />
          </button>
        </div>
        {/* Progress bar */}
        {p.percent_complete != null && (
          <div className="mt-3 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-teal-500 to-emerald-500 transition-all duration-500"
              style={{ width: `${p.percent_complete}%` }} />
          </div>
        )}
      </Card>

      {/* Body: Map + Side panel */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
        {/* Map column */}
        <div className="lg:col-span-3 space-y-3">
          {/* Filters + playback */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex gap-1">
              {TIME_FILTERS.map((f) => (
                <button key={f.key} onClick={() => setFilter(f.key)}
                  data-testid={`route-filter-${f.key}`}
                  className={`text-[10px] font-mono px-2.5 py-1 rounded-sm border transition-colors ${
                    filter === f.key
                      ? "bg-teal-500/20 text-teal-400 border-teal-700"
                      : "bg-zinc-900 text-zinc-400 border-zinc-800 hover:border-zinc-700"}`}>
                  {f.label}
                </button>
              ))}
            </div>
            <div className="flex-1" />
            {/* Playback control */}
            <div className="flex items-center gap-2 bg-zinc-900 border border-zinc-800 rounded-sm px-2 py-1">
              <button onClick={() => { setPlayIdx(0); setPlaying(true); }}
                className="text-zinc-300 hover:text-teal-400" title="Replay" data-testid="route-play-btn">
                {playing ? <Pause size={12} weight="fill" /> : <Play size={12} weight="fill" />}
              </button>
              <input type="range" min={0} max={Math.max(pts.length - 1, 0)} value={playIdx}
                onChange={(e) => { setPlaying(false); setPlayIdx(Number(e.target.value)); }}
                className="w-32 accent-teal-500" data-testid="route-scrubber" />
              <span className="text-[9px] font-mono text-zinc-500 min-w-[60px]">
                {playIdx + 1}/{pts.length}
              </span>
            </div>
          </div>

          {/* Map */}
          <Card className="bg-zinc-900 border-zinc-800 p-0 relative">
            <div className="absolute top-3 right-3 z-[1000] flex border border-zinc-700 rounded overflow-hidden shadow-lg">
              <button onClick={() => setBasemap("dark")} className={`px-3 py-1.5 text-xs font-medium ${basemap === "dark" ? "bg-teal-500 text-zinc-950" : "bg-zinc-900 text-zinc-200"}`}>Dark</button>
              <button onClick={() => setBasemap("satellite")} className={`px-3 py-1.5 text-xs font-medium ${basemap === "satellite" ? "bg-teal-500 text-zinc-950" : "bg-zinc-900 text-zinc-200"}`}>Satellite</button>
            </div>
            <div ref={mapRef} className="w-full rounded-sm" style={{ height: "560px", minHeight: "560px", background: "#1A1A1A" }}
              data-testid="route-map" />

            {/* Recenter button — user-controlled, sits below default zoom controls */}
            <button onClick={recenter}
              title="Recenter route"
              data-testid="route-recenter-btn"
              className="absolute top-[88px] left-[10px] w-[34px] h-[34px] rounded-sm bg-zinc-900/95 border-2 border-zinc-700 hover:border-teal-500 hover:bg-zinc-800 text-zinc-300 hover:text-teal-400 flex items-center justify-center shadow-lg transition-all z-[1000] backdrop-blur-sm group"
              style={{ boxShadow: "0 1px 5px rgba(0,0,0,0.65)" }}>
              <Crosshair size={16} weight="bold" />
              <span className="absolute left-[42px] top-1/2 -translate-y-1/2 px-2 py-1 bg-zinc-950 border border-zinc-800 rounded-sm text-[10px] font-mono text-zinc-300 whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity">
                Recenter route
              </span>
            </button>

            {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-zinc-950/40 backdrop-blur-sm">
                <p className="text-xs font-mono text-teal-400 animate-pulse">Loading route...</p>
              </div>
            )}
          </Card>

          {/* Legend */}
          <Card className="bg-zinc-900 border-zinc-800 p-3">
            <button onClick={() => setLegendOpen(!legendOpen)}
              className="w-full flex items-center justify-between text-[10px] uppercase tracking-wider text-zinc-400 font-bold"
              data-testid="route-legend-toggle">
              <span className="flex items-center gap-1.5"><Path size={10} /> Legend</span>
              <span className="text-zinc-600">{legendOpen ? "−" : "+"}</span>
            </button>
            {legendOpen && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3 text-[10px] font-mono text-zinc-400">
                <LegendRow swatch={<div className="h-0.5 w-6 bg-teal-500" />} label="Completed (actual)" />
                <LegendRow swatch={<div className="h-0.5 w-6" style={{ borderTop: "2px dashed #52525B" }} />} label="Remaining (planned)" />
                <LegendRow swatch={<div className="h-0.5 w-6 bg-red-500" />} label="Route w/ alerts" />
                <LegendRow swatch={<div className="h-0.5 w-6" style={{ borderTop: "2px dashed #71717A" }} />} label="Planned route (faint)" />
                {Object.entries(ALERT_KIND_STYLES).map(([k, s]) => (
                  <LegendRow key={k} swatch={<div className="w-3 h-3 rounded-full" style={{ background: s.color }} />} label={`Alert · ${s.label}`} />
                ))}
                <LegendRow swatch={<div className="w-3 h-3 rounded-full bg-emerald-500 border-2 border-zinc-950" />} label="Origin" />
                <LegendRow swatch={<div className="w-3 h-3 rounded-full bg-red-500 border-2 border-zinc-950" />} label="Destination" />
              </div>
            )}
          </Card>
        </div>

        {/* Side panel: route points list */}
        <Card className="bg-zinc-900 border-zinc-800 p-0 lg:col-span-1 flex flex-col" style={{ maxHeight: "700px" }}>
          <div className="px-3 py-2 border-b border-zinc-800">
            <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400 flex items-center gap-1.5">
              <ListChecks size={10} /> Route Points <span className="text-zinc-600 ml-auto">{pts.length}</span>
            </h3>
          </div>
          <div className="flex-1 overflow-y-auto" data-testid="route-points-list">
            {pts.length === 0 ? (
              <p className="text-xs text-zinc-600 text-center py-6">No GPS points yet</p>
            ) : (
              pts.map((pt, i) => {
                const ts = (pt.timestamp || "").substring(0, 19).replace("T", " ");
                const isActive = i === playIdx || i === hoverIdx;
                return (
                  <button key={i} onClick={() => { setPlayIdx(i); setPlaying(false); focusPoint(i); }}
                    onMouseEnter={() => setHoverIdx(i)} onMouseLeave={() => setHoverIdx(null)}
                    data-testid={`route-point-${i}`}
                    className={`w-full text-left px-3 py-1.5 border-b border-zinc-900 text-[10px] font-mono transition-colors ${
                      isActive ? "bg-teal-500/10 border-l-2 border-l-teal-400" :
                      pt.has_alert ? "hover:bg-zinc-800 border-l-2 border-l-red-500/40" :
                      "hover:bg-zinc-800 border-l-2 border-l-transparent"}`}>
                    <div className="flex items-center gap-2 text-zinc-300">
                      <span className={`w-1 h-1 rounded-full ${pt.has_alert ? "bg-red-400" : "bg-teal-400"}`} />
                      <span className="text-zinc-500">{ts}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 ml-3 text-zinc-500">
                      {pt.temperature != null && (
                        <span className={pt.flags.temp_excursion ? "text-amber-400" : ""}>{pt.temperature}°F</span>
                      )}
                      {pt.flags.light_breach && <Lightbulb size={9} className="text-amber-400" />}
                      {pt.flags.battery_low && <span className="text-amber-400">bat</span>}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

function ProgressKPI({ label, value, color }) {
  return (
    <div>
      <p className={`text-base font-mono font-bold leading-none ${color}`}>{value}</p>
      <p className="text-[9px] uppercase tracking-wider text-zinc-500 mt-1">{label}</p>
    </div>
  );
}

function LegendRow({ swatch, label }) {
  return (
    <div className="flex items-center gap-2">
      {swatch}
      <span>{label}</span>
    </div>
  );
}
